import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Modal,
  RiskBadge,
  TextAreaField,
  TextField,
  useToast,
} from '@csa/ui';
import type { PermissionKey } from '@csa/shared';
import { ApiError, api } from '../../lib/api.js';
import { SodFindings } from './SodFindings.js';
import type { PermissionCatalogue, RoleView, SodAnalysisView } from './types.js';

interface TargetOption {
  id: string;
  key: string;
  name: string;
  kind: string;
  environment: string;
}

/**
 * The role editor.
 *
 * Two things it does that a plain permission checklist does not:
 *
 *  - It evaluates segregation of duties live, as permissions are ticked,
 *    rather than only on submit. An administrator finding out at the end that
 *    the combination is refused has already made every other decision around
 *    it and will look for a way around the block instead of a way to the
 *    right answer.
 *  - It makes target scoping unavoidable. A privileged permission renders its
 *    target picker inline and the form cannot be submitted while one is
 *    empty, because a scoped permission with no targets grants nothing and
 *    would otherwise sit in the role looking meaningful.
 */
export function RoleEditor({
  role,
  catalogue,
  onClose,
}: {
  role: RoleView | null;
  catalogue: PermissionCatalogue;
  onClose: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const readOnly = role?.isBuiltin ?? false;

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [permissions, setPermissions] = useState<Set<PermissionKey>>(
    () => new Set(role?.permissions ?? []),
  );
  const [grants, setGrants] = useState<Map<PermissionKey, Set<string>>>(() => {
    const initial = new Map<PermissionKey, Set<string>>();
    for (const grant of role?.targetGrants ?? []) {
      const set = initial.get(grant.permission) ?? new Set<string>();
      set.add(grant.targetId);
      initial.set(grant.permission, set);
    }
    return initial;
  });
  const [justification, setJustification] = useState(role?.sodJustification ?? '');
  const [serverError, setServerError] = useState<string | null>(null);

  const targets = useQuery({
    queryKey: ['admin', 'targets', 'options'],
    queryFn: () => api.get<{ targets: TargetOption[] }>('/api/admin/target-options'),
    staleTime: 5 * 60_000,
  });

  /**
   * Live SoD evaluation, computed from the same catalogue the server uses.
   * The server re-runs it and is the authority; this exists so the answer is
   * visible while the decision is being made.
   */
  const sod = useMemo<SodAnalysisView>(() => {
    const found = catalogue.toxicCombinations
      .filter((c) => permissions.has(c.a) && permissions.has(c.b))
      .map((c) => ({
        ...c,
        aLabel: catalogue.permissions.find((p) => p.key === c.a)?.label ?? c.a,
        bLabel: catalogue.permissions.find((p) => p.key === c.b)?.label ?? c.b,
      }));
    return {
      blocking: found.filter((f) => f.severity === 'block'),
      warnings: found.filter((f) => f.severity === 'warn'),
      clean: found.length === 0,
    };
  }, [permissions, catalogue]);

  const unscoped = useMemo(
    () =>
      catalogue.permissions.filter(
        (p) => p.requiresTargetScope && permissions.has(p.key) && !grants.get(p.key)?.size,
      ),
    [permissions, grants, catalogue],
  );

  const needsJustification = sod.warnings.length > 0;
  const canSubmit =
    !readOnly &&
    name.trim().length >= 3 &&
    sod.blocking.length === 0 &&
    unscoped.length === 0 &&
    (!needsJustification || justification.trim().length >= 20);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        description: description.trim(),
        permissions: [...permissions],
        targetGrants: [...grants].flatMap(([permission, targetIds]) =>
          [...targetIds].map((targetId) => ({ permission, targetId })),
        ),
        sodJustification: needsJustification ? justification.trim() : null,
      };
      return role
        ? api.put<{ roleId: string }>(`/api/admin/roles/${role.id}`, body)
        : api.post<{ roleId: string }>('/api/admin/roles', body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
      toast({
        tone: 'success',
        title: role ? 'Role updated' : 'Role created',
        message: `"${name.trim()}" is now in effect for everyone holding it.`,
      });
      onClose();
    },
    onError: (err: unknown) => {
      setServerError(
        err instanceof ApiError ? err.message : 'The role could not be saved. Try again.',
      );
    },
  });

  function togglePermission(key: PermissionKey, checked: boolean): void {
    setPermissions((current) => {
      const next = new Set(current);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
    // Dropping a permission drops its target scoping with it, otherwise the
    // grants silently reappear if the permission is ticked again later.
    if (!checked) {
      setGrants((current) => {
        const next = new Map(current);
        next.delete(key);
        return next;
      });
    }
  }

  function toggleTarget(key: PermissionKey, targetId: string, checked: boolean): void {
    setGrants((current) => {
      const next = new Map(current);
      const set = new Set(next.get(key) ?? []);
      if (checked) set.add(targetId);
      else set.delete(targetId);
      next.set(key, set);
      return next;
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={role ? `${readOnly ? 'View' : 'Edit'} role — ${role.name}` : 'New role'}
      size="lg"
      footer={
        <>
          <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {readOnly ? null : (
            <Button
              variant="primary"
              onClick={() => save.mutate()}
              disabled={!canSubmit}
              loading={save.isPending}
            >
              {role ? 'Save changes' : 'Create role'}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {readOnly ? (
          <Alert tone="info" title="Built-in role">
            Built-in roles define the separation of duties documented in the control matrix and
            cannot be edited. Create a new role if you need a variation.
          </Alert>
        ) : null}

        {serverError ? (
          <Alert tone="danger" title="Could not save">
            {serverError}
          </Alert>
        ) : null}

        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={readOnly}
          required
          maxLength={80}
        />

        <TextAreaField
          label="Description"
          description="Explain who this role is for. An access reviewer reads this before deciding whether an assignment is appropriate."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={readOnly}
          rows={2}
          maxLength={1000}
        />

        <SodFindings analysis={sod} context="role" />

        {unscoped.length > 0 ? (
          <Alert tone="warning" title="Select at least one target">
            <p>
              These permissions only take effect on specific connection targets. Until a target is
              chosen they grant nothing:
            </p>
            <ul className="mt-1 list-disc pl-5">
              {unscoped.map((p) => (
                <li key={p.key}>{p.label}</li>
              ))}
            </ul>
          </Alert>
        ) : null}

        <fieldset className="flex flex-col gap-5" disabled={readOnly}>
          <legend className="sr-only">Permissions</legend>

          {catalogue.categories.map((category) => {
            const inCategory = catalogue.permissions.filter((p) => p.category === category);
            if (inCategory.length === 0) return null;

            return (
              <div key={category} className="flex flex-col gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-subtle">
                  {category}
                </h3>

                <div className="flex flex-col divide-y divide-line rounded-md border border-line">
                  {inCategory.map((permission) => {
                    const checked = permissions.has(permission.key);
                    const selected = grants.get(permission.key) ?? new Set<string>();

                    return (
                      <div key={permission.key} className="p-3">
                        <Checkbox
                          checked={checked}
                          onChange={(e) => togglePermission(permission.key, e.target.checked)}
                          label={
                            <span className="flex flex-wrap items-center gap-2">
                              {permission.label}
                              <RiskBadge risk={permission.risk} />
                              {permission.requiresTargetScope ? (
                                <Badge tone="neutral">Per target</Badge>
                              ) : null}
                            </span>
                          }
                          description={permission.description}
                        />

                        {checked && permission.requiresTargetScope ? (
                          <div className="ml-6 mt-3 rounded-md bg-sunken p-3">
                            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-body">
                              {selected.size === 0 ? (
                                <ExclamationTriangleIcon
                                  className="size-3.5 text-brand"
                                  aria-hidden="true"
                                />
                              ) : null}
                              Effective on
                            </p>

                            {targets.isPending ? (
                              <p className="text-xs text-subtle">Loading targets…</p>
                            ) : targets.data && targets.data.targets.length > 0 ? (
                              <div className="grid gap-1.5 sm:grid-cols-2">
                                {targets.data.targets.map((target) => (
                                  <Checkbox
                                    key={target.id}
                                    checked={selected.has(target.id)}
                                    onChange={(e) =>
                                      toggleTarget(permission.key, target.id, e.target.checked)
                                    }
                                    label={
                                      <span className="flex items-center gap-2 text-xs">
                                        {target.name}
                                        <Badge tone="neutral">{target.environment}</Badge>
                                      </span>
                                    }
                                  />
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-subtle">
                                No connection targets are configured yet. Add one before granting
                                this permission.
                              </p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </fieldset>

        {needsJustification ? (
          <TextAreaField
            label="Why is this concentration of privilege acceptable?"
            description="At least a sentence. This text appears verbatim in the access review, so write it for someone who was not in the conversation."
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            disabled={readOnly}
            rows={3}
            required
            error={
              justification.trim().length > 0 && justification.trim().length < 20
                ? 'Give a fuller explanation.'
                : undefined
            }
          />
        ) : null}
      </div>
    </Modal>
  );
}
